import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ArrowDownToLine,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Cpu,
  Package,
  Server,
  ExternalLink,
  Loader2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  agentUpdatesApi,
  type AgentVersionInfo,
  type UpdateResult,
} from "@/api/agent-updates";
import {
  adminRuntimeApi,
  type RuntimeFleetAgent,
  type RuntimeSweep,
  type RuntimeSweepResult,
  type RuntimeUpdateResult,
} from "@/api/admin-runtime";
import { ApiError } from "@/api/client";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  active: "success",
  running: "success",
  idle: "secondary",
  paused: "warning",
  error: "danger",
  terminated: "danger",
  pending_approval: "warning",
};

const stepLabels: Record<string, string> = {
  backup_config: "Backup config",
  backup_workspace: "Backup workspace",
  npm_update: "Install update",
  verify_version: "Verify version",
  restore_config: "Restore config",
  restore_workspace: "Restore workspace",
  restart_gateway: "Restart gateway",
};

export function AgentUpdates() {
  const queryClient = useQueryClient();
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [updateResults, setUpdateResults] = useState<Record<string, UpdateResult>>({});

  // Fetch latest version
  const {
    data: latestVersion,
    isLoading: loadingVersion,
    refetch: refetchVersion,
  } = useQuery({
    queryKey: ["agent-updates", "latest-version"],
    queryFn: agentUpdatesApi.getLatestVersion,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all agent versions
  const {
    data: agentVersions = [],
    isLoading: loadingAgents,
    refetch: refetchAgents,
  } = useQuery({
    queryKey: ["agent-updates", "agents"],
    queryFn: agentUpdatesApi.listAgentVersions,
    staleTime: 30 * 1000,
  });

  // Check all versions mutation
  const checkAllMutation = useMutation({
    mutationFn: agentUpdatesApi.checkAllVersions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
  });

  // Check single agent version
  const checkVersionMutation = useMutation({
    mutationFn: (agentId: string) => agentUpdatesApi.checkAgentVersion(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
  });

  // Update agent mutation. The server runs the update detached from the
  // HTTP request, so we poll the status endpoint and stream progress into
  // updateResults so the expanded row shows step-by-step output live.
  const updateMutation = useMutation({
    mutationFn: (agentId: string) =>
      agentUpdatesApi.updateAgent(agentId, {
        onProgress: (job) => {
          setUpdateResults((prev) => ({
            ...prev,
            [agentId]: {
              success: job.status === "success",
              agentId: job.agentId,
              previousVersion: job.previousVersion,
              newVersion: job.newVersion,
              steps: job.steps,
              error: job.error,
            },
          }));
        },
      }),
    onSuccess: (result) => {
      setUpdateResults((prev) => ({ ...prev, [result.agentId]: result }));
      queryClient.invalidateQueries({ queryKey: ["agent-updates", "agents"] });
    },
    onError: (_err, agentId) => {
      setUpdateResults((prev) => ({
        ...prev,
        [agentId]: {
          success: false,
          agentId,
          previousVersion: null,
          newVersion: null,
          steps: [],
          error: _err instanceof Error ? _err.message : "Update failed",
        },
      }));
    },
  });

  // Auto-check versions once on first load if any agents show "Unknown"
  const hasAutoChecked = useRef(false);
  useEffect(() => {
    if (
      !hasAutoChecked.current &&
      !loadingAgents &&
      agentVersions.length > 0 &&
      agentVersions.some((a) => !a.currentVersion)
    ) {
      hasAutoChecked.current = true;
      checkAllMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAgents, agentVersions]);

  const outdatedCount = agentVersions.filter((a) => a.updateAvailable).length;
  const activeAgents = agentVersions.filter(
    (a) => a.status === "active" || a.status === "idle" || a.status === "running",
  );

  return (
    <CenterPanel title="Agent Updates">
      {/* ProClaw Runtime fleet */}
      <RuntimeFleetSection />

      {/* Latest version card */}
      <Card className="mb-6">
        <CardContent className="py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
                <Package size={24} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-text-muted">Latest OpenClaw Version</p>
                {loadingVersion ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Loader2 size={14} className="animate-spin text-text-muted" />
                    <span className="text-sm text-text-secondary">Checking...</span>
                  </div>
                ) : latestVersion ? (
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xl font-semibold text-text-primary font-mono">
                      v{latestVersion.version}
                    </span>
                    <Badge variant={latestVersion.source === "cache" ? "secondary" : "success"}>
                      {latestVersion.source}
                    </Badge>
                    {latestVersion.publishedAt && (
                      <span className="text-xs text-text-muted">
                        Released {new Date(latestVersion.publishedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-danger">Failed to fetch</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {latestVersion?.htmlUrl && (
                <a
                  href={latestVersion.htmlUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                >
                  View release <ExternalLink size={12} />
                </a>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchVersion()}
                disabled={loadingVersion}
              >
                <RefreshCw size={14} className={loadingVersion ? "animate-spin" : ""} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary + actions */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <p className="text-sm text-text-secondary">
            {agentVersions.length} agent{agentVersions.length !== 1 ? "s" : ""} tracked
          </p>
          {outdatedCount > 0 && (
            <Badge variant="warning">
              {outdatedCount} update{outdatedCount !== 1 ? "s" : ""} available
            </Badge>
          )}
          {outdatedCount === 0 && agentVersions.length > 0 && (
            <Badge variant="success">All up to date</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => checkAllMutation.mutate()}
            disabled={checkAllMutation.isPending}
          >
            {checkAllMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Check All Versions
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchAgents();
              refetchVersion();
            }}
            disabled={loadingAgents}
          >
            <RefreshCw size={14} className={loadingAgents ? "animate-spin" : ""} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Agent list */}
      {loadingAgents ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 size={24} className="animate-spin text-text-muted" />
          </CardContent>
        </Card>
      ) : agentVersions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Server size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">
              No OpenClaw agents found. Deploy agents from within a tenant workspace.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {agentVersions.map((agent) => (
            <AgentRow
              key={agent.agentId}
              agent={agent}
              expanded={expandedAgent === agent.agentId}
              onToggle={() =>
                setExpandedAgent(expandedAgent === agent.agentId ? null : agent.agentId)
              }
              updateResult={updateResults[agent.agentId]}
              isUpdating={updateMutation.isPending && updateMutation.variables === agent.agentId}
              isChecking={checkVersionMutation.isPending && checkVersionMutation.variables === agent.agentId}
              onCheckVersion={() => checkVersionMutation.mutate(agent.agentId)}
              onUpdate={() => {
                setExpandedAgent(agent.agentId);
                updateMutation.mutate(agent.agentId);
              }}
            />
          ))}
        </div>
      )}

      {/* Release notes */}
      {latestVersion?.releaseNotes && (
        <Card className="mt-6">
          <CardContent className="py-4">
            <h3 className="text-sm font-medium text-text-primary mb-2">Release Notes</h3>
            <pre className="text-xs text-text-secondary whitespace-pre-wrap break-words font-mono bg-surface-hover rounded-lg p-3 max-h-60 overflow-auto">
              {latestVersion.releaseNotes}
            </pre>
          </CardContent>
        </Card>
      )}
    </CenterPanel>
  );
}

function RuntimeFleetSection() {
  const queryClient = useQueryClient();
  const [force, setForce] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [hostResults, setHostResults] = useState<Record<string, RuntimeUpdateResult>>({});

  const {
    data: fleet,
    isLoading: loadingFleet,
    isFetching: fetchingFleet,
    refetch: refetchFleet,
  } = useQuery({
    queryKey: ["admin-runtime", "fleet"],
    queryFn: adminRuntimeApi.getFleet,
    // The fleet probe costs a 3.5s timeout per dark VM, so poll only while a
    // sweep is running — otherwise fetch on mount / manual refresh.
    refetchInterval: (query) => (query.state.data?.activeSweep ? 3000 : false),
  });

  const sweepActive = !!fleet?.activeSweep;

  const updateAllMutation = useMutation({
    mutationFn: () => adminRuntimeApi.updateAll(force),
    onSuccess: () => {
      setNotice(null);
      // 202 — the sweep runs in the background; refetching flips activeSweep
      // on, which starts the 3s polling above.
      queryClient.invalidateQueries({ queryKey: ["admin-runtime", "fleet"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setNotice("A sweep is already running — showing its progress.");
        queryClient.invalidateQueries({ queryKey: ["admin-runtime", "fleet"] });
      } else {
        setNotice(err instanceof Error ? err.message : "Failed to start sweep");
      }
    },
  });

  const updateHostMutation = useMutation({
    mutationFn: (agentId: string) => adminRuntimeApi.updateHost(agentId, force),
    onSuccess: (result, agentId) => {
      setHostResults((prev) => ({ ...prev, [agentId]: result }));
      queryClient.invalidateQueries({ queryKey: ["admin-runtime", "fleet"] });
    },
    onError: (err, agentId) => {
      setHostResults((prev) => ({
        ...prev,
        [agentId]: {
          success: false,
          updated: false,
          error: err instanceof Error ? err.message : "Update failed",
        },
      }));
    },
  });

  // Host-level counts — parent + sub-agents share one runtime process per VM,
  // so a host counts once regardless of how many agent rows it carries.
  const hostStatus = new Map<string, { reachable: boolean; current: boolean }>();
  for (const a of fleet?.agents ?? []) {
    const key = a.host ?? `agent:${a.agentId}`;
    if (!hostStatus.has(key)) hostStatus.set(key, { reachable: a.reachable, current: a.current });
  }
  let currentCount = 0;
  let staleCount = 0;
  let unreachableCount = 0;
  for (const s of hostStatus.values()) {
    if (!s.reachable) unreachableCount++;
    else if (s.current) currentCount++;
    else staleCount++;
  }

  // Parents first per host so sub-agent rows sit under their host row.
  const rows = [...(fleet?.agents ?? [])].sort((a, b) => {
    const h = (a.host ?? "").localeCompare(b.host ?? "");
    if (h !== 0) return h;
    if (!!a.parentAgentId !== !!b.parentAgentId) return a.parentAgentId ? 1 : -1;
    return a.name.localeCompare(b.name);
  });
  const hostsWithParent = new Set(
    rows.filter((r) => !r.parentAgentId && r.host).map((r) => r.host as string),
  );

  const hasDist = !!fleet?.expected;

  return (
    <Card className="mb-6">
      <CardContent className="py-5">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10">
              <Cpu size={24} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-text-muted">ProClaw Runtime fleet</p>
              {loadingFleet ? (
                <div className="flex items-center gap-2 mt-1">
                  <Loader2 size={14} className="animate-spin text-text-muted" />
                  <span className="text-sm text-text-secondary">Probing fleet...</span>
                </div>
              ) : (
                <div className="flex items-center gap-3 mt-1">
                  {hasDist ? (
                    <span className="text-xl font-semibold text-text-primary font-mono">
                      {fleet?.expected}
                    </span>
                  ) : (
                    <span className="text-sm text-text-muted">
                      No runtime dist in this deployment
                    </span>
                  )}
                  {currentCount > 0 && <Badge variant="success">{currentCount} current</Badge>}
                  {staleCount > 0 && <Badge variant="warning">{staleCount} stale</Badge>}
                  {unreachableCount > 0 && (
                    <Badge variant="danger">{unreachableCount} unreachable</Badge>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-text-secondary">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                disabled={sweepActive}
              />
              Force re-stage even if version matches
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetchFleet()}
              disabled={fetchingFleet}
            >
              <RefreshCw size={14} className={fetchingFleet ? "animate-spin" : ""} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => updateAllMutation.mutate()}
              disabled={sweepActive || !hasDist || updateAllMutation.isPending}
              title={!hasDist ? "No runtime dist built on this deployment" : "Re-stage every stale host"}
            >
              {updateAllMutation.isPending || sweepActive ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowDownToLine size={14} />
              )}
              Update all
            </Button>
          </div>
        </div>

        {notice && <p className="mt-3 text-xs text-warning">{notice}</p>}

        {/* Live sweep progress */}
        {fleet?.activeSweep && (
          <div className="mt-4 rounded-lg bg-surface-hover p-3">
            <div className="flex items-center gap-2 text-sm text-text-primary">
              <Loader2 size={14} className="animate-spin text-accent" />
              <span className="font-medium">
                Sweep running — {fleet.activeSweep.results.length} / {fleet.activeSweep.hosts} hosts
              </span>
              <span className="text-xs text-text-muted">
                started {new Date(fleet.activeSweep.startedAt).toLocaleTimeString()}
                {fleet.activeSweep.force ? " · force" : ""}
              </span>
            </div>
            {fleet.activeSweep.results.length > 0 && (
              <div className="mt-2">
                <SweepResultLine
                  result={fleet.activeSweep.results[fleet.activeSweep.results.length - 1]}
                />
              </div>
            )}
          </div>
        )}

        {/* Last sweep summary */}
        {!fleet?.activeSweep && fleet?.lastSweep && (
          <SweepSummary sweep={fleet.lastSweep} />
        )}

        {/* Fleet table */}
        {!loadingFleet && rows.length === 0 && (
          <p className="mt-4 text-sm text-text-secondary">
            No ProClaw Runtime agents found.
          </p>
        )}
        {rows.length > 0 && (
          <div className="mt-4 divide-y divide-border border-t border-border">
            {rows.map((agent) => (
              <RuntimeFleetRow
                key={agent.agentId}
                agent={agent}
                expected={fleet?.expected ?? null}
                showUpdateButton={
                  !agent.parentAgentId || !agent.host || !hostsWithParent.has(agent.host)
                }
                result={hostResults[agent.agentId]}
                isUpdating={
                  updateHostMutation.isPending && updateHostMutation.variables === agent.agentId
                }
                disabled={sweepActive || updateHostMutation.isPending}
                onUpdate={() => updateHostMutation.mutate(agent.agentId)}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SweepResultLine({ result }: { result: RuntimeSweepResult }) {
  const failed = result.success === false || !!result.error;
  const label = result.error
    ? result.error
    : result.skipped
      ? "skipped"
      : result.updated
        ? `updated${result.version ? ` → ${result.version}` : ""}`
        : "already current";
  return (
    <div className="flex items-center gap-2 text-xs">
      {failed ? (
        <AlertCircle size={12} className="text-danger shrink-0" />
      ) : result.skipped ? (
        <AlertCircle size={12} className="text-warning shrink-0" />
      ) : (
        <CheckCircle2 size={12} className="text-success shrink-0" />
      )}
      <span className="font-mono text-text-secondary">{result.host}</span>
      <span className="text-text-muted">({result.name})</span>
      <span className={failed ? "text-danger" : "text-text-muted"}>{label}</span>
    </div>
  );
}

function SweepSummary({ sweep }: { sweep: RuntimeSweep }) {
  const okCount = sweep.results.filter((r) => r.success !== false && !r.skipped).length;
  const skippedCount = sweep.results.filter((r) => r.skipped).length;
  const failedCount = sweep.results.filter((r) => r.success === false || !!r.error).length;
  return (
    <div className="mt-4 rounded-lg bg-surface-hover p-3">
      <div className="flex items-center gap-2 text-sm">
        {failedCount > 0 || sweep.aborted ? (
          <AlertCircle size={16} className="text-danger shrink-0" />
        ) : (
          <CheckCircle2 size={16} className="text-success shrink-0" />
        )}
        <span className="font-medium text-text-primary">Last sweep</span>
        <span className="text-xs text-text-muted">
          {sweep.finishedAt
            ? `finished ${new Date(sweep.finishedAt).toLocaleString()}`
            : `started ${new Date(sweep.startedAt).toLocaleString()}`}
          {sweep.force ? " · force" : ""}
        </span>
        <span className="text-xs text-text-secondary">
          {okCount} ok · {skippedCount} skipped · {failedCount} failed
        </span>
      </div>
      {sweep.aborted && (
        <div className="mt-2 flex items-center gap-2 rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
          <AlertCircle size={12} className="shrink-0" />
          <span>Sweep aborted: {sweep.aborted}</span>
        </div>
      )}
      {sweep.results.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {sweep.results.map((r, i) => (
            <SweepResultLine key={i} result={r} />
          ))}
        </div>
      )}
    </div>
  );
}

function RuntimeFleetRow({
  agent,
  expected,
  showUpdateButton,
  result,
  isUpdating,
  disabled,
  onUpdate,
}: {
  agent: RuntimeFleetAgent;
  expected: string | null;
  showUpdateButton: boolean;
  result?: RuntimeUpdateResult;
  isUpdating: boolean;
  disabled: boolean;
  onUpdate: () => void;
}) {
  const isSubAgent = !!agent.parentAgentId;
  return (
    <div className="py-3">
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-3 ${isSubAgent ? "pl-8" : ""}`}>
          <Server size={16} className={isSubAgent ? "text-text-muted" : "text-accent"} />
          <div>
            <p className="text-sm font-medium text-text-primary">
              {agent.name}
              {isSubAgent && (
                <span className="ml-2 text-xs font-normal text-text-muted">
                  sub-agent of host
                </span>
              )}
            </p>
            <p className="text-xs text-text-muted font-mono">{agent.host ?? "No host"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-sm text-text-primary">
            {agent.version ?? "—"}
          </span>
          {!agent.reachable ? (
            <Badge variant="danger" className="whitespace-nowrap">
              <AlertCircle size={12} className="mr-1 shrink-0" />
              Unreachable
            </Badge>
          ) : agent.current ? (
            <Badge variant="success" className="whitespace-nowrap">
              <CheckCircle2 size={12} className="mr-1 shrink-0" />
              Current
            </Badge>
          ) : (
            <Badge variant="warning" className="whitespace-nowrap">
              <AlertCircle size={12} className="mr-1 shrink-0" />
              {expected ? "Stale" : "Unknown"}
            </Badge>
          )}
          {showUpdateButton && (
            <Button
              variant={agent.reachable && !agent.current ? "default" : "outline"}
              size="sm"
              onClick={onUpdate}
              disabled={disabled}
              title="Re-stage the runtime dist on this host"
            >
              {isUpdating ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <ArrowDownToLine size={14} />
                  Update
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      {result && (
        <div className={`mt-1.5 text-xs ${isSubAgent ? "pl-8" : ""} ${result.success ? "text-text-muted" : "text-danger"}`}>
          {result.error
            ? result.error
            : result.skipped
              ? "Skipped"
              : result.updated
                ? `Updated${result.version ? ` → ${result.version}` : ""}`
                : "Already current"}
        </div>
      )}
    </div>
  );
}

function AgentRow({
  agent,
  expanded,
  onToggle,
  updateResult,
  isUpdating,
  isChecking,
  onCheckVersion,
  onUpdate,
}: {
  agent: AgentVersionInfo;
  expanded: boolean;
  onToggle: () => void;
  updateResult?: UpdateResult;
  isUpdating: boolean;
  isChecking: boolean;
  onCheckVersion: () => void;
  onUpdate: () => void;
}) {
  const isActive = agent.status === "active" || agent.status === "idle" || agent.status === "running";

  return (
    <Card>
      <CardContent className="py-0">
        {/* Main row */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              <Server size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">{agent.agentName}</p>
              <p className="text-xs text-text-muted">
                {agent.companyName} · {agent.ipAddress || "No IP"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Version display */}
            <div className="text-right mr-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-muted">Current:</span>
                <span className="font-mono text-sm text-text-primary">
                  {agent.currentVersion ? `v${agent.currentVersion}` : "Unknown"}
                </span>
              </div>
              {agent.updateAvailable && agent.currentVersion && (
                <div className="flex items-center gap-1 mt-0.5">
                  <ArrowDownToLine size={10} className="text-warning" />
                  <span className="text-[11px] text-warning">
                    v{agent.latestVersion} available
                  </span>
                </div>
              )}
            </div>

            {/* Status badge */}
            <Badge variant={statusVariant[agent.status] ?? "default"}>
              {agent.status}
            </Badge>

            {/* Version status badge */}
            {agent.currentVersion && !agent.updateAvailable && (
              <Badge variant="success" className="whitespace-nowrap">
                <CheckCircle2 size={12} className="mr-1 shrink-0" />
                Up to date
              </Badge>
            )}
            {agent.updateAvailable && (
              <Badge variant="warning" className="whitespace-nowrap">
                <AlertCircle size={12} className="mr-1 shrink-0" />
                Update available
              </Badge>
            )}

            {/* Actions */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onCheckVersion}
              disabled={isChecking || !isActive}
              title="Check installed version"
            >
              {isChecking ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
            </Button>

            <Button
              variant={agent.updateAvailable ? "default" : "outline"}
              size="sm"
              onClick={onUpdate}
              disabled={isUpdating || !isActive || !agent.updateAvailable}
              title={!isActive ? "Agent must be active to update" : "Update OpenClaw"}
            >
              {isUpdating ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <ArrowDownToLine size={14} />
                  Update
                </>
              )}
            </Button>

            {/* Expand/collapse */}
            <Button variant="ghost" size="icon" onClick={onToggle}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="border-t border-border py-4">
            {updateResult ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  {updateResult.success ? (
                    <CheckCircle2 size={16} className="text-success" />
                  ) : (
                    <AlertCircle size={16} className="text-danger" />
                  )}
                  <span className="text-sm font-medium text-text-primary">
                    {updateResult.success ? "Update completed" : "Update failed"}
                  </span>
                  {updateResult.previousVersion && updateResult.newVersion && (
                    <span className="text-xs text-text-muted">
                      v{updateResult.previousVersion} → v{updateResult.newVersion}
                    </span>
                  )}
                </div>
                {updateResult.error && (
                  <p className="text-xs text-danger mb-3">{updateResult.error}</p>
                )}
                <div className="space-y-1.5">
                  {updateResult.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {step.success ? (
                        <CheckCircle2 size={12} className="text-success shrink-0" />
                      ) : (
                        <AlertCircle size={12} className="text-danger shrink-0" />
                      )}
                      <span className="text-text-secondary font-medium w-32">
                        {stepLabels[step.step] || step.step}
                      </span>
                      <span className="text-text-muted truncate">
                        {step.error || step.output || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-xs text-text-muted">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-text-secondary">Agent ID:</span>{" "}
                    <span className="font-mono">{agent.agentId.slice(0, 8)}...</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Tenant:</span> {agent.companyName}
                  </div>
                  <div>
                    <span className="text-text-secondary">IP Address:</span>{" "}
                    <span className="font-mono">{agent.ipAddress || "—"}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Last Checked:</span>{" "}
                    {agent.lastCheckedAt
                      ? new Date(agent.lastCheckedAt).toLocaleString()
                      : "Never"}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
